import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { buyerSelector, setupAuthOngoingSelector } from '@src/features/customers';
import {
  currentOrderSelector,
  hasPendingSaleRequiredFormInputsSelector,
  loadPendingOrders,
  loadReturnOrder,
  resumeDirectLinkOrder,
} from '@src/features/orders';
import { currentSaleSelector, loadReturnSale } from '@src/stores/sale';
import { currentSaleChangeSelector } from '@src/stores/saleChange';

import { resolveCheckoutEntryMode } from '../core/resolveCheckoutEntryMode';
import { CheckoutMode } from '../types';

type UseCheckoutBootstrapParams = {
  mode: CheckoutMode;
  initialMode: CheckoutMode;
  skipConfirmation: boolean;
  /** Automatic step change — replaces the current entry under routing, so back never bounces. */
  redirectTo: (mode: CheckoutMode) => void;
};

type UseCheckoutBootstrapResult = {
  /** The checkout has not settled on a step yet: show a placeholder, not a step. */
  bootstrapping: boolean;
};

/**
 * Opens the checkout on the right step. The session check and the purchase lookups both land after
 * the first render, so until they do the checkout has no way of knowing what the buyer is here for
 * — it used to guess, paint the auth step, and correct itself a few steps later in full view.
 */
export const useCheckoutBootstrap = (params: UseCheckoutBootstrapParams): UseCheckoutBootstrapResult => {
  const { mode, initialMode, skipConfirmation, redirectTo } = params;

  const dispatch = useAppDispatch();
  const [, logged] = useAppSelector(buyerSelector);
  const setupAuthOngoing = useAppSelector(setupAuthOngoingSelector);
  const order = useAppSelector(currentOrderSelector);
  const sale = useAppSelector(currentSaleSelector);
  const saleChange = useAppSelector(currentSaleChangeSelector);
  const pendingFormInputs = useAppSelector(hasPendingSaleRequiredFormInputsSelector);

  const [purchasesLoaded, setPurchasesLoaded] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!logged) {
      return undefined;
    }

    let active = true;

    Promise.all([
      dispatch(loadPendingOrders()),
      dispatch(loadReturnSale()),
      // The link order goes after the return order, which wins, and before the checkout settles on a
      // step: without it the checkout would check the cart out again.
      Promise.resolve(dispatch(loadReturnOrder())).then(() => dispatch(resumeDirectLinkOrder())),
    ])
      .catch((error) => {
        console.error('Could not load the buyer purchases for checkout bootstrap:', error);
      })
      .finally(() => {
        if (active) {
          setPurchasesLoaded(true);
        }
      });

    return () => {
      active = false;
    };
  }, [logged, dispatch]);

  // A guest has nothing to look up, so the checkout is ready as soon as the session check comes
  // back empty; a logged buyer additionally needs their pending purchases resolved.
  const ready = !setupAuthOngoing && (!logged || purchasesLoaded);

  // Recomputed until the checkout lands on it, then left alone: from there on the buyer owns the
  // navigation and `useCheckoutModeDecision` owns the automatic step changes.
  const entryMode =
    ready && !entered
      ? resolveCheckoutEntryMode({
          initialMode,
          logged: Boolean(logged),
          skipConfirmation,
          order,
          sale,
          saleChange,
          pendingFormInputs,
        })
      : null;

  useEffect(() => {
    if (entryMode == null) {
      return;
    }

    if (entryMode !== mode) {
      redirectTo(entryMode);
      return;
    }

    setEntered(true);
  }, [entryMode, mode, redirectTo]);

  return { bootstrapping: !ready || (entryMode != null && entryMode !== mode) };
};

export default useCheckoutBootstrap;
